import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { capitalize, groupBy } from 'lodash';
import { Box, Button, Flex, FlexColumn, Grid, Heading, Tag, Text } from 'core/components';
import { dateTimeFormatter } from 'core/util/dateUtils';
import { CONDITION_TYPE_LABELS, flaggedAsnRoleText } from '@kentik/ui-shared/synthetics/bgp';
import { ALARM_NULL_END_DATE } from '@kentik/ui-shared/alerting/constants';
import { isAlarmActive } from '@kentik/ui-shared/synthetics/utils';
import LabelValue from 'app/views/metrics/components/LabelValue';
import ASNRenderer from 'app/components/asn/ASNRenderer';
import KentikAgentDrawerSection from 'app/views/settings/kentikAgent/agentDrawer/KentikAgentDrawerSection';
import VantagePointList from 'app/views/synthetics/components/bgp/VantagePointList';
import { legacyImpactFields } from 'app/views/synthetics/utils/bgpLegacy';
import { alarmElapsed, formatAlarmElapsed } from 'app/views/synthetics/utils/alarmElapsed';
import { alarmStateRenderer, severityRenderer } from 'app/views/synthetics/utils/alarmsTableRenderers';

const LABEL_WIDTH = 130;

@observer
class BgpAlarmDetails extends Component {
  get alarmType() {
    const { alarm } = this.props;
    const subtest = alarm.get('key.value.subtest');

    if (subtest) {
      return CONDITION_TYPE_LABELS[subtest] || 'Unknown';
    }

    return alarm.get('details.bgp.display') || 'Unknown';
  }

  get bgp() {
    const { alarm } = this.props;

    return alarm.get('details.bgp') || {};
  }

  get monitoredPrefix() {
    const { alarm } = this.props;
    const { prefix, monitoredPrefix } = this.bgp;

    return monitoredPrefix || prefix || alarm.get('key.value.prefix') || null;
  }

  get affectedPrefixes() {
    const { affectedPrefixes, affectedPrefixesTruncated } = this.bgp;

    if (!Array.isArray(affectedPrefixes) && !affectedPrefixesTruncated) {
      return null;
    }

    const prefixes = Array.isArray(affectedPrefixes) ? affectedPrefixes : [];
    // the affected prefix list is only interesting when it says something the monitored prefix doesn't
    const isJustMonitoredPrefix = prefixes.length === 1 && prefixes[0] === this.monitoredPrefix;

    if (isJustMonitoredPrefix && !affectedPrefixesTruncated) {
      return null;
    }

    return this.buildValueWithTruncation({
      value: this.buildTagList(prefixes),
      truncated: affectedPrefixesTruncated,
      truncatedMessage: 'Additional affected prefixes were omitted.'
    });
  }

  get rpkiStatuses() {
    const { rpkiStatuses } = this.bgp;

    if (!Array.isArray(rpkiStatuses)) {
      return null;
    }

    return this.buildTagList(rpkiStatuses.map((status) => capitalize(status)));
  }

  get impactFields() {
    const { vantagePoints, vpCount, currentReachability } = this.bgp;

    return [
      { label: 'Monitored Prefix', value: this.monitoredPrefix },
      { label: 'Affected Prefixes', value: this.affectedPrefixes },
      { label: 'Vantage Points', value: vantagePoints ?? vpCount ?? null },
      { label: 'Reachability', value: currentReachability ?? null },
      { label: 'RPKI Status', value: this.rpkiStatuses },
      ...legacyImpactFields(this.bgp)
    ];
  }

  /*
    alert manager reports the absence of an end time either by omitting the field (v1 alarms) or by
    sending the ALARM_NULL_END_DATE sentinel (v2); both mean there is no usable end timestamp
  */
  get endTime() {
    const { alarm } = this.props;
    const endTime = alarm.get('end_time');

    return endTime && endTime !== ALARM_NULL_END_DATE ? endTime : null;
  }

  get duration() {
    const { alarm } = this.props;
    const elapsed = alarmElapsed({
      startTime: alarm.get('start_time'),
      endTime: alarm.get('end_time'),
      state: alarm.get('state')
    });

    if (!elapsed) {
      return null;
    }

    return `${formatAlarmElapsed(elapsed)}${elapsed.isActive ? ' and ongoing' : ''}`;
  }

  get timingFields() {
    const { alarm } = this.props;
    const startTime = alarm.get('start_time');
    const { endTime } = this;
    const isActive = isAlarmActive(alarm.get('state'));

    return [
      { label: 'Start', value: startTime ? dateTimeFormatter({ value: startTime }) : null },
      { label: 'End', value: isActive ? 'Active' : (endTime && dateTimeFormatter({ value: endTime })) || null },
      { label: 'Duration', value: this.duration }
    ];
  }

  /*
    TODO: an 'Alarm ID' row linking to /v4/synthetics/alerts/<id> would be useful here, but the
    synthetics alerts page can't render BGP alarms yet, so the link leads nowhere. Add it back once
    that page handles BGP alarms.
  */
  buildTagList(values) {
    if (!Array.isArray(values) || values.length === 0) {
      return null;
    }

    return (
      <Flex flexWrap="wrap" gap="4px">
        {values.map((value) => (
          <Tag key={value} minimal>
            {value}
          </Tag>
        ))}
      </Flex>
    );
  }

  buildValueWithTruncation({ value, truncated, truncatedMessage }) {
    if (!value && !truncated) {
      return null;
    }

    return (
      <FlexColumn gap={1}>
        {value}
        {truncated && (
          <Text muted fontStyle="italic" small>
            {truncatedMessage}
          </Text>
        )}
      </FlexColumn>
    );
  }

  /*
    Findings group under their prefix, since an alarm on one monitored prefix usually holds several
    and repeating the prefix per row buries the part that differs. The heading is dropped where
    every finding is on the prefix the impact section already names, matching what affectedPrefixes
    does with the same fact. The grid keeps each ASN and its role aligned down the section.
  */
  buildRoutingIssuesSection() {
    const { routingIssues, routingIssuesTruncated } = this.bgp;

    if (!Array.isArray(routingIssues) || routingIssues.length === 0) {
      return null;
    }

    // rows arrive ranked by VP count, so the busiest prefix heads the section
    const byPrefix = groupBy(routingIssues, 'prefix');
    const prefixes = Object.keys(byPrefix);
    const showPrefix = prefixes.length > 1 || prefixes[0] !== this.monitoredPrefix;

    return (
      <KentikAgentDrawerSection title={`Routing Issues (${routingIssues.length}${routingIssuesTruncated ? '+' : ''})`}>
        <FlexColumn gap="10px" maxHeight={280} overflow="auto">
          {Object.entries(byPrefix).map(([prefix, rows]) => (
            <Box key={prefix}>
              {showPrefix && (
                <Text as="div" fontWeight="bold" small>
                  {prefix}
                </Text>
              )}
              <Grid
                gridTemplateColumns="auto 1fr"
                gridColumnGap={1}
                gridRowGap="2px"
                alignItems="center"
                ml={showPrefix ? 1 : 0}
                mt={showPrefix ? '2px' : 0}
              >
                {rows.map((row) => (
                  <React.Fragment key={`${row.asn}-${row.roleLabel}-${row.roleQualifier}-${row.vpCount}`}>
                    {/* the name matches the role beside it, leaving the AS tag as the anchor */}
                    <ASNRenderer asn={row.asn} showLinks maxWidth="100%" fontSize={12} gap="2px" />
                    <Flex alignItems="center" flexWrap="wrap" gap="4px">
                      <Text muted small>
                        {flaggedAsnRoleText(row)}
                        {Number.isFinite(row.vpCount) && ` (${row.vpCount} ${row.vpCount === 1 ? 'VP' : 'VPs'})`}
                      </Text>
                      {row.rpkiStatus && <Tag minimal>{capitalize(row.rpkiStatus)}</Tag>}
                    </Flex>
                  </React.Fragment>
                ))}
              </Grid>
            </Box>
          ))}
          {routingIssuesTruncated && (
            <Text muted fontStyle="italic" small>
              Additional routing issues were omitted.
            </Text>
          )}
        </FlexColumn>
      </KentikAgentDrawerSection>
    );
  }

  buildVantagePointSection({ title, values, truncated }) {
    if (!Array.isArray(values) || values.length === 0) {
      return null;
    }

    return (
      <KentikAgentDrawerSection
        title={`${title} (${values.length}${truncated ? '+' : ''})`}
        openOnMount={false}
        keepChildrenUnMounted
      >
        <VantagePointList names={values} truncated={truncated} />
      </KentikAgentDrawerSection>
    );
  }

  buildLabelValues(fields) {
    return fields
      .filter(({ value }) => value != null && value !== '')
      .map(({ label, value }) => (
        <LabelValue
          key={label}
          label={label}
          value={value}
          labelWidth={LABEL_WIDTH}
          mb={0}
          alignItems="center"
          // several values are block-level elements, which don't lay out correctly in the default inline span
          valueTextProps={{ as: 'div', width: '100%' }}
        />
      ));
  }

  buildSection(title, fields) {
    const rendered = this.buildLabelValues(fields);

    if (rendered.length === 0) {
      return null;
    }

    return (
      <KentikAgentDrawerSection title={title}>
        <FlexColumn gap="12px">{rendered}</FlexColumn>
      </KentikAgentDrawerSection>
    );
  }

  render() {
    const { alarm, onClose } = this.props;

    if (!alarm) {
      return null;
    }

    const severity = alarm.get('severity');
    const state = alarm.get('state');

    return (
      <Flex flexDirection="column" height="100%" overflow="auto">
        <Flex alignItems="center" pt={2} px="28px" justifyContent="space-between">
          <Button onClick={onClose} minWidth={70} text="Close" />
        </Flex>
        <FlexColumn alignItems="center" gap="4px" px="28px" pb={3} borderBottom="thin" mb={1}>
          <Text muted>Alarm Details</Text>
          <Heading level={4} fontWeight="heavy" m={0} textAlign="center">
            {this.alarmType}
          </Heading>
          <Flex gap="8px" alignItems="center">
            {severity && <Box>{severityRenderer()({ value: severity })}</Box>}
            {state && <Box>{alarmStateRenderer()({ value: state })}</Box>}
          </Flex>
        </FlexColumn>

        {/* ktrac's one sentence reads as the alarm's headline rather than as one field among many */}
        {this.bgp.summary && (
          <FlexColumn px="28px" pt={2} pb={3} borderBottom="thin">
            <Text large>{this.bgp.summary}</Text>
          </FlexColumn>
        )}

        {this.buildSection('Impact', this.impactFields)}

        {this.buildRoutingIssuesSection()}

        {this.buildSection('Timing', this.timingFields)}

        {this.buildVantagePointSection({
          title: 'Unreachable Vantage Points',
          values: this.bgp.unreachableVps,
          truncated: this.bgp.unreachableVpsTruncated
        })}
        {this.buildVantagePointSection({
          title: 'Affected Vantage Points',
          values: this.bgp.affectedVps,
          truncated: this.bgp.affectedVpsTruncated
        })}
      </Flex>
    );
  }
}

export default BgpAlarmDetails;
